import { useInfiniteQuery, useQuery } from "react-query"
import { getEventQuestion, getEventQuestionResponses, getEventQuestions } from "api/forms"
import { FormQuestion } from "../sharedTypes"

// Omits `per` entirely when unset (rather than a literal `undefined` array
// element) so refetchEventQuestions's invalidateQueries(eventQuestionsCacheKey(eventId))
// — a shorter key — still prefix-matches every `per` variant.
export const eventQuestionsCacheKey = (eventId: string, per?: number) => (
  per ? ["eventQuestions", eventId, per] : ["eventQuestions", eventId]
)
export const eventQuestionCacheKey = (questionId: string, page?: number) => ["eventQuestion", questionId, page]

// `per` lets a caller request a bigger page size than the API default.
export const useEventQuestions = (eventId: string, per?: number) => {
  const {
    data, fetchNextPage, isLoading,
  } = useInfiniteQuery(
    eventQuestionsCacheKey(eventId, per),
    ({ pageParam = 1 }) => getEventQuestions(eventId, pageParam, per),
    {
      getNextPageParam: (
        lastPage,
      ) => (lastPage.data.meta.nextPage ?? undefined),
    },
  )

  const questions: FormQuestion[] = data?.pages.reduce(
    (prev, curr) => ([...prev, ...curr.data.data]),
      [] as FormQuestion[],
  ) || []

  return {
    questions, fetchNextPage, isLoading,
  }
}

export const useEventQuestion = (eventId: string, questionId: string) => useQuery(
  eventQuestionCacheKey(questionId),
  () => getEventQuestion(eventId, questionId).then(({ data }) => data),
  { enabled: Boolean(questionId) },
)

export const useEventQuestionResponsesPaginated = (
  eventId: string,
  questionId: string,
  page: number,
) => useQuery(
  eventQuestionCacheKey(questionId, page),
  () => getEventQuestionResponses(eventId, questionId, page).then(({ data }) => data),
  { keepPreviousData: true },
)
